import {
  Control,
  Controller,
  FieldError,
  FieldErrors,
  RegisterOptions,
  useWatch,
} from "react-hook-form";
import { ErrorMessage } from "./ErrorMessage";
import {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
  createContext,
  useContext,
} from "react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/shadcn/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/shadcn/popover";
import { Button } from "@/components/shadcn/button";
import {
  Check,
  ChevronDown,
  Clock,
  ExternalLink,
  GitBranch,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { WorkflowService, WorkflowVersion } from "@/generated";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import {
  CURRENT_VERSION,
  LEGACY_CURRENT_VERSION,
  versionIdForApi,
} from "@/utils/versions";
import { CurrentVersionWarning } from "./CurrentVersionWarning";
import { timeAgo } from "@/utils/generic";
import { Skeleton } from "@/components/shadcn/skeleton";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useRouter } from "next/navigation";
import Link from "next/link";

const MAX_VERSIONS_PAGE = 10;

interface WorkflowVersionOption {
  value: string;
  label: string;
  version: WorkflowVersion;
}

interface IWorkflowVersionInputProps {
  label?: string;
  placeholder?: string;
  tooltip?: string;
  value?: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  name?: string;
  onlyAssistantSupport?: boolean;
  disableDeselection?: boolean;
  allowCurrentVersion?: boolean;
  // Deployment/trigger call sites: warn while the selection tracks the flow's
  // current definition. Off elsewhere ("current" just means "the live draft").
  warnOnCurrentVersion?: boolean;
  disablePortal?: boolean;
  invalidTitle?: string;
  invalidReasons?: string[];
  flowHref?: string;
  onValidityChange?: (isInvalid: boolean) => void;
  // Suppress the built-in "invalid version" banner below the select — the
  // consumer surfaces the state itself (e.g. an alert above a table) via
  // onValidityChange.
  hideInvalidBanner?: boolean;
}

export const WorkflowVersionInput = (props: IWorkflowVersionInputProps) => {
  // const workflowId = props.workflowId;
  const workflowId = useContext(WorkflowVersionContext);
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { spotGroup } = useSpotUser();
  const router = useRouter();

  const observer = useRef<IntersectionObserver>(undefined);

  const hasPortal = !props.disablePortal;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isError,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [DATA_KEYS.WORKFLOW_VERSIONS, workflowId, spotGroup.id],
    queryFn: ({ pageParam = 1 }) =>
      WorkflowService.getWorkflowVersions({
        path: {
          group_id: spotGroup.id,
          workflow_id: workflowId || "",
        },
        query: {
          page: pageParam,
          size: MAX_VERSIONS_PAGE,
          assistant_support: props.onlyAssistantSupport,
        },
      }),
    enabled: !!workflowId,
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.data.page) return;
      return firstPage.data.page > 1 ? firstPage.data.page - 1 : undefined;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data.page || !lastPage.data.pages) return;

      return lastPage.data.page < lastPage.data.pages
        ? allPages.length + 1
        : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  });

  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading || isFetching) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetching, isLoading]
  );

  const versions = useMemo(() => {
    const vs = data?.pages?.flatMap((page) => page.data.items);
    if (props.allowCurrentVersion) {
      return [
        {
          id: CURRENT_VERSION,
          name: "Current version",
        },
        ...(vs || []),
      ];
    }
    return vs;
  }, [data?.pages, props.allowCurrentVersion]);

  const filteredVersions = useMemo(() => {
    if (!versions) return [];
    if (!searchValue.trim()) return versions;

    const filtered = versions.filter((version) =>
      version.name.toLowerCase().includes(searchValue.toLowerCase())
    );

    return filtered;
  }, [versions, searchValue]);

  // An empty value already POSTs as null, so when the synthetic row is offered
  // it IS the selection - a "Select version..." placeholder would misstate it.
  const effectiveValue =
    props.allowCurrentVersion && !props.value ? CURRENT_VERSION : props.value;

  const selectedVersion = useMemo(() => {
    return versions?.find((v) => v.id === effectiveValue);
  }, [versions, effectiveValue]);

  const versionIdForValidation =
    props.value && props.value !== CURRENT_VERSION ? props.value : undefined;
  const { data: validationResult } = useQuery({
    queryKey: [DATA_KEYS.WORKFLOWS_ID, "valid", workflowId, props.value],
    queryFn: () =>
      WorkflowService.getIsValidWorkflow({
        path: { group_id: spotGroup.id, workflow_id: workflowId || "" },
        query: { version_id: versionIdForValidation },
      }),
    enabled: !!workflowId && props.invalidReasons === undefined,
  });

  const isSelectedInvalid = validationResult?.data?.is_valid === false;
  const effectiveInvalid = isSelectedInvalid || !!props.invalidReasons?.length;

  const onValidityChange = props.onValidityChange;
  useEffect(() => {
    onValidityChange?.(effectiveInvalid);
  }, [effectiveInvalid, onValidityChange]);

  const invalidReasons = props.invalidReasons?.length
    ? props.invalidReasons
    : isSelectedInvalid
      ? [
          "This version is invalid and can't be used. Please select a valid version.",
        ]
      : [];

  const { data: workflowData } = useRemoteData(
    DATA_KEYS.WORKFLOWS_ID,
    {
      path: {
        group_id: spotGroup.id,
        workflow_id: workflowId || "",
      },
    },
    {
      enabled: !!workflowId && props.allowCurrentVersion,
    }
  );

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (!workflowId) {
    return (
      <NoxusAlert
        look="banner"
        title="The workflow version is not set. Please contact support."
        variant="destructive"
      />
    );
  }

  return (
    <div className="w-full">
      {props.label && (
        <NoxusFormLabel
          label={props.label}
          tooltip={props.tooltip}
          name={props.name}
        />
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            data-testid="workflow-version-select"
            className="w-full justify-between font-normal"
            disabled={props.isDisabled}
          >
            {selectedVersion?.name || props.placeholder || "Select version..."}
            <ChevronDown
              className={cn(
                "opacity-50 transition-transform duration-200",
                open && "rotate-180"
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[400px] p-0 z-5000 pointer-events-auto"
          align="start"
          hasPortal={hasPortal}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search versions..."
              className="h-9"
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                <div>
                  <p className="mb-2">
                    {isError
                      ? "Error loading versions"
                      : isLoading
                        ? "Loading..."
                        : searchValue.trim()
                          ? "No versions match your search."
                          : "No versions found."}
                  </p>
                  {!isError && !isLoading && !searchValue.trim() && (
                    <Link
                      href={`/flows/${workflowId}?createVersion=true`}
                      target="_blank"
                      className="underline"
                    >
                      <Button variant="outline">Create a new version</Button>
                    </Link>
                  )}
                </div>
              </CommandEmpty>
              <CommandGroup className="max-h-[250px] overflow-y-auto overflow-x-hidden">
                {!isLoading &&
                  filteredVersions?.map((version, index) => {
                    return (
                      <CommandItem
                        key={version.id}
                        value={version.id}
                        onSelect={(currentValue) => {
                          if (
                            props.disableDeselection &&
                            currentValue === effectiveValue
                          ) {
                            // Don't allow deselection when disableDeselection is true
                            setOpen(false);
                            return;
                          }
                          const deselected = currentValue === effectiveValue;
                          props.onChange(
                            deselected
                              ? props.allowCurrentVersion
                                ? CURRENT_VERSION
                                : ""
                              : currentValue
                          );
                          setOpen(false);
                        }}
                      >
                        {version.id === CURRENT_VERSION ? (
                          <GitBranch size={16} className="mr-2" />
                        ) : (
                          <Clock size={16} className="mr-2" />
                        )}
                        <div
                          className="flex flex-col items-start flex-1"
                          ref={
                            index === filteredVersions.length - 1
                              ? lastElementRef
                              : undefined
                          }
                        >
                          <span className="font-medium text-overflow-hidden text-ellipsis whitespace-nowrap">
                            {version.name}
                          </span>
                          {(version as WorkflowVersion)?.created_at && (
                            <span className="text-xs text-muted-foreground">
                              Saved{" "}
                              {timeAgo(
                                (version as WorkflowVersion).created_at
                              ).toLowerCase()}
                            </span>
                          )}
                          {version.id === CURRENT_VERSION &&
                            workflowData &&
                            !!workflowData.last_updated_at && (
                              <span className="text-xs text-muted-foreground">
                                Saved{" "}
                                {timeAgo(
                                  workflowData.last_updated_at
                                ).toLowerCase()}
                              </span>
                            )}
                        </div>
                        <Check
                          className={cn(
                            "ml-auto",
                            effectiveValue === version.id
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                      </CommandItem>
                    );
                  })}

                {isFetchingNextPage && (
                  <div className="p-2">
                    <Skeleton className="h-8" />
                  </div>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {props.warnOnCurrentVersion && versionIdForApi(props.value) === null && (
        <CurrentVersionWarning entity="workflow" />
      )}
      {!props.hideInvalidBanner && invalidReasons.length > 0 && (
        <NoxusAlert
          className="mt-2"
          variant="warning"
          title={props.invalidTitle}
          description={
            invalidReasons.length === 1 && !props.invalidTitle ? (
              invalidReasons[0]
            ) : (
              <ul className="list-disc pl-4">
                {invalidReasons.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            )
          }
          action={
            props.flowHref
              ? {
                  label: (
                    <>
                      <ExternalLink className="h-3.5 w-3.5" />
                      Go to flow
                    </>
                  ),
                  onClick: () =>
                    window.open(
                      props.flowHref,
                      "_blank",
                      "noopener,noreferrer"
                    ),
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

interface IWorkflowVersionFieldProps {
  name: string;
  tooltip?: string;
  label?: string;
  placeholder?: string;
  rules?: RegisterOptions;
  errors?: FieldErrors<any>;
  control: Control<any>;
  defaultValue?: string;
  onChange?: (value: string) => void;
  isDisabled?: boolean;
  disableDeselection?: boolean;
  allowCurrentVersion?: boolean;
  warnOnCurrentVersion?: boolean;
  // NCL config values may carry the subflow's legacy "latest" sentinel;
  // set by the config_workflow_version renderer only.
  mapLegacyCurrent?: boolean;
}

export const WorkflowVersionField = (props: IWorkflowVersionFieldProps) => {
  const hasError = !!(props.errors && props.errors[props.name]);

  return (
    <div className="w-full flex flex-col gap-1 items-start">
      <Controller
        control={props.control}
        disabled={props.isDisabled}
        defaultValue={props.defaultValue}
        name={props.name}
        rules={props.rules}
        render={({ field: { onChange, value } }) => (
          <WorkflowVersionInput
            value={
              props.mapLegacyCurrent && value === LEGACY_CURRENT_VERSION
                ? CURRENT_VERSION
                : value
            }
            label={props.label}
            placeholder={props.placeholder}
            isDisabled={props.isDisabled}
            tooltip={props.tooltip}
            onChange={(value) => {
              onChange(value);
              if (props.onChange) props.onChange(value);
            }}
            name={props.name}
            disableDeselection={props.disableDeselection}
            allowCurrentVersion={props.allowCurrentVersion}
            warnOnCurrentVersion={props.warnOnCurrentVersion}
          />
        )}
      />
      {hasError && (
        <ErrorMessage error={props.errors![props.name] as FieldError} />
      )}
    </div>
  );
};

export const WorkflowVersionContext = createContext<string | undefined>(
  undefined
);

// Version field whose workflow comes from a sibling form field (e.g. the
// subflow node's `workflow_id` config) instead of the ambient context.
export const SiblingWorkflowVersionField = ({
  workflowFieldName,
  ...props
}: IWorkflowVersionFieldProps & { workflowFieldName: string }) => {
  const workflowId = useWatch({
    control: props.control,
    name: workflowFieldName,
  });
  return (
    <WorkflowVersionContext.Provider
      value={
        typeof workflowId === "string" && workflowId ? workflowId : undefined
      }
    >
      <WorkflowVersionField {...props} />
    </WorkflowVersionContext.Provider>
  );
};
